/**
 * Supplier Statement Reconciliation (FSD 2.2.2.10 "supplier-reported balance against ledger
 * balance with differences itemised"; FT10-U16 over the FT9-B31 routes).
 *
 * Thin wrappers over the generated client:
 *
 *  - `ap.supplierStatements.list|get` and `.reconciliation` need `ap.report.read`;
 *  - `ap.supplierStatements.create` (JSON lines) and `.import` (a CSV file, multipart)
 *    need `ap.statement_recon.manage`. A recorded statement is never edited or deleted;
 *    the same vendor, currency, date and reference twice is 409 ERR_AP_STATEMENT_DUPLICATE.
 *
 * The reconciliation is computed afresh by the server on every read. The one sum this
 * module does — {@link reconIdentity} — re-adds the server's own component totals with
 * decimal.js to show the reader that they account for the whole difference.
 */

import {
  apSupplierStatementsCreate,
  apSupplierStatementsImport,
  apSupplierStatementsList,
  apSupplierStatementsReconciliation,
  type ApCreateSupplierStatementRequest,
  type ApStatementReconDTO,
  type ApSupplierStatementDTO,
  type ApSupplierStatementLineInput,
  type ApSupplierStatementListQuery,
  type PageApSupplierStatementDTO,
} from '@erpx/api'
import Decimal from 'decimal.js'

import { api } from '@/api/client'

export type {
  ApCreateSupplierStatementRequest,
  ApStatementReconDTO,
  ApSupplierStatementDTO,
  ApSupplierStatementLineInput,
  PageApSupplierStatementDTO,
}

export const STATEMENT_RECON_PERMISSIONS = {
  reportRead: 'ap.report.read',
  /** Record a statement, as JSON lines or a CSV file. Posts nothing. */
  manage: 'ap.statement_recon.manage',
  vendorRead: 'ap.vendor.read',
  companyRead: 'org.company.read',
} as const

export const STATEMENT_DUPLICATE_CODE = 'ERR_AP_STATEMENT_DUPLICATE'

/** `ApStatementLineKind` — an empty kind is recorded as `other`. */
export const STATEMENT_LINE_KINDS = ['invoice', 'credit_note', 'debit_note', 'payment', 'other'] as const
export type StatementLineKind = (typeof STATEMENT_LINE_KINDS)[number]

/** The server's `statementMaxLines` bound is enforced there; this is only the page size. */
export const STATEMENTS_PAGE_SIZE = 50

export interface StatementListFilters {
  companyId: number
  vendorId: number | null
  from: string
  to: string
  cursor: string
}

export const STATEMENT_RECON_KEYS = {
  all: ['ap', 'statement-recon'] as const,
  list: (filters: StatementListFilters) => ['ap', 'statement-recon', 'list', filters] as const,
  recon: (statementId: number) => ['ap', 'statement-recon', 'recon', statementId] as const,
} as const

export function listStatements(filters: StatementListFilters): Promise<PageApSupplierStatementDTO> {
  const query: ApSupplierStatementListQuery = {
    companyId: filters.companyId,
    limit: STATEMENTS_PAGE_SIZE,
    ...(filters.vendorId === null ? {} : { vendorId: filters.vendorId }),
    ...(filters.from === '' ? {} : { from: filters.from }),
    ...(filters.to === '' ? {} : { to: filters.to }),
    ...(filters.cursor === '' ? {} : { cursor: filters.cursor }),
  }
  return apSupplierStatementsList(api(), query)
}

export function reconcileStatement(statementId: number): Promise<ApStatementReconDTO> {
  return apSupplierStatementsReconciliation(api(), { id: String(statementId) })
}

export function recordStatement(
  body: ApCreateSupplierStatementRequest,
  idempotencyKey: string,
): Promise<ApSupplierStatementDTO> {
  return apSupplierStatementsCreate(api(), body, idempotencyKey)
}

export interface StatementHeader {
  companyId: number
  vendorId: number
  currency: string
  statementDate: string
  reportedBalance: string
  periodFrom: string
  openingBalance: string
  statementRef: string
  notes: string
}

/** Only the optional header fields that carry a value travel. */
export function importStatement(
  file: File,
  header: StatementHeader,
  idempotencyKey: string,
): Promise<ApSupplierStatementDTO> {
  return apSupplierStatementsImport(
    api(),
    file,
    {
      companyId: header.companyId,
      vendorId: header.vendorId,
      currency: header.currency,
      statementDate: header.statementDate,
      reportedBalance: header.reportedBalance,
      ...(header.periodFrom === '' ? {} : { periodFrom: header.periodFrom }),
      ...(header.openingBalance === '' ? {} : { openingBalance: header.openingBalance }),
      ...(header.statementRef === '' ? {} : { statementRef: header.statementRef }),
      ...(header.notes === '' ? {} : { notes: header.notes }),
    },
    idempotencyKey,
  )
}

/** A signed money string: `1250.00`, `-300`, `0.5`. Two decimals at most. */
export function isMoney(value: string): boolean {
  return /^-?\d{1,16}(\.\d{1,2})?$/.test(value.trim())
}

export function isDate(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value)
}

export interface ReconIdentityTerm {
  key: 'opening' | 'supplierOnly' | 'oursOnly' | 'amountDifferences' | 'settlementDifferences' | 'unexplained'
  /** How the term enters the sum. */
  sign: '+' | '-'
  /** The server's figure, verbatim. */
  value: string
}

export interface ReconIdentity {
  terms: ReconIdentityTerm[]
  /** The terms re-added (decimal.js), to two places. */
  sum: string
  /** The server's supplier balance less ledger balance. */
  difference: string
  holds: boolean
}

/**
 * The reconciliation identity, as `internal/ap/statement_recon.go` builds it:
 *
 *   supplier balance − ledger balance
 *     = opening difference + supplier-only − ours-only
 *       + amount differences + settlement differences + unexplained
 *
 * Every term is the server's string; only the sum is computed here, with decimal.js.
 */
export function reconIdentity(recon: ApStatementReconDTO): ReconIdentity {
  const terms: ReconIdentityTerm[] = [
    { key: 'opening', sign: '+', value: recon.openingDifference },
    { key: 'supplierOnly', sign: '+', value: recon.supplierOnlyTotal },
    { key: 'oursOnly', sign: '-', value: recon.oursOnlyTotal },
    { key: 'amountDifferences', sign: '+', value: recon.amountDifferencesTotal },
    { key: 'settlementDifferences', sign: '+', value: recon.settlementDifferencesTotal },
    { key: 'unexplained', sign: '+', value: recon.unexplainedDifference },
  ]
  const sum = terms.reduce(
    (total, term) => (term.sign === '+' ? total.plus(term.value) : total.minus(term.value)),
    new Decimal(0),
  )
  return {
    terms,
    sum: sum.toFixed(2),
    difference: recon.difference,
    holds: sum.equals(new Decimal(recon.difference)),
  }
}
